<template>
  <PopupForm
    title="Print Labels"
    button="Print"
    :show="show"
    @close="emit('close')"
    @confirm="printLabel()"
  >
    <div class="input-group col-span-12 sm:col-span-4 mb-4">
      <select
        v-model="form.location_id"
        name="location_id"
      >
        <template v-for="location in locationList">
          <option :value="location.id">{{ location.name }}</option>
        </template>
      </select>
      <p
        v-if="getErrors('location_id')"
        class="input-error"
      >
        {{ getErrors('location_id') }}
      </p>
    </div>

    <div class="input-group col-span-12 sm:col-span-4 mb-4">
      <select
        v-model="form.printer_id"
        name="printer_id"
      >
        <template v-for="printer in printerList">
          <option :value="printer.id">{{ printer.name }}</option>
        </template>
      </select>
      <p
        v-if="getErrors('printer_id')"
        class="input-error"
      >
        {{ getErrors('printer_id') }}
      </p>
    </div>

    <div class="input-group col-span-12 sm:col-span-4 mb-4">
      <select
        v-model="form.mode"
        name="mode"
      >
        <option :value="null">Select Code Type</option>
        <template v-for="(label, key) in modes">
          <option :value="key">
            {{ label }}
          </option>
        </template>
      </select>
      <p
        v-if="getErrors('mode')"
        class="input-error"
      >
        {{ getErrors('mode') }}
      </p>
    </div>

    <div
      v-for="(pkg, key) in form.packages"
      class="grid grid-cols-12 gap-4 py-3 items-center"
    >
      <div
        class="text-left flex flex-col leading-tight"
        :class="{ 'col-span-6': usesSubLot, 'col-span-10': !usesSubLot }"
      >
        <span
          class="uppercase font-semibold text-sm"
          :class="{ 'text-red-600': false, 'text-blue-600': true }"
        >
          {{ getProductName(pkg.id) || getPackageName(pkg.id) || '' }}
        </span>
        <span class="text-gray-600 text-sm font-semibold">
          {{ getPackageSubLotNum(pkg.id) || '' }}
        </span>
      </div>
      <div
        v-if="usesSubLot"
        class="input-group col-span-4"
      >
        <select
          v-model="form.packages[key].sub_lot_num"
          name="sub_lot_num"
        >
          <option :value="null">Select Sub-Lot</option>
          <template v-for="(label, key) in subLotOptionsForPackage(pkg.id)">
            <option :value="label">
              {{ label }}
            </option>
          </template>
        </select>
        <p
          v-if="getErrors(`packages.${key}.sub_lot_num`)"
          class="input-error"
        >
          {{ getErrors(`packages.${key}.sub_lot_num`) }}
        </p>
      </div>
      <div class="input-group col-span-2 text-center">
        <input
          v-model="form.packages[key].quantity"
          type="number"
          step="1"
          min="0"
          :class="{ 'border-red-600 border-[1.5px] text-red-600': false, 'border border-gray-400 text-blue-600': true }"
        />
      </div>
    </div>
  </PopupForm>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { useForm } from '@inertiajs/vue3'
import PopupForm from '@/Components/Forms/PopupForm'
import { getHeadersWithToken } from '@/utils/http'
import _ from 'lodash'

const emit = defineEmits(['close'])

const props = defineProps({
  /**
   * If true we display the form
   */
  show: {
    type: Boolean,
    default: false
  },
  showOnlyNewSubLots: {
    type: Boolean,
    default: false
  },
  /**
   * An array of package objects with, minimum, { id, name, batch_num } or { id, root: { name, batch_num } }
   */
  packages: {
    type: Array,
    required: true
  },
  /**
   * A list of locations we can transfer a package to
   */
  locations: {
    type: Array,
    default: () => []
  },
  /**
   * A list of print modes for label.
   */
  modes: {
    type: Array,
    default: () => []
  },
  /**
   * The location to pull sub-lot numbers for (instead of selected location).
   */
  labelLocationId: {
    type: Number,
    default: null
  }
})

const formPackagesFromProps = () => {
  if (!props.showOnlyNewSubLots) {
    return props.packages
  }

  return props.packages
    .filter((pkg) => pkg.parent_sub_lot_num !== pkg.sub_lot_num)
    .map((pkg) => ({
      sub_lot_num: null,
      quantity: pkg.transfer_quantity_adjusted,
      ..._.pick(pkg, ['id', 'sub_lot_num'])
    }))
}

const form = useForm({
  packages: formPackagesFromProps(),
  location_id: null,
  printer_id: null,
  mode: null
})

const submitting = ref(false)
const printers = ref([])
const subLotNums = ref({})

const locationList = computed(() => {
  return [{ name: 'Select A Location', id: null }, ...props.locations]
})
const printerList = computed(() => {
  return [{ name: 'Select A Printer', id: null }, ...printers.value]
})
const usesSubLot = computed(() => {
  return ['sub_lot', 'tag_and_sub_lot'].includes(form.mode)
})

watch(props.packages, (newVal) => {
  if (newVal) {
    form.packages = formPackagesFromProps()
  }
})

watch(
  () => form.location_id,
  (newVal) => {
    if (newVal) {
      getPrintersForLocation(newVal)
    } else {
      printers.value = []
      form.printer_id = null
    }

    if (usesSubLot.value) {
      getSubLotsForPackages()
    }
  }
)

watch(usesSubLot, (newVal) => {
  if (newVal) {
    getSubLotsForPackages()
  }
})

async function getPrintersForLocation(location_id) {
  const response = await fetch(route('api.locations.printers', location_id), {
    headers: getHeadersWithToken(),
    method: 'get'
  })

  printers.value = await response.json()
}

async function getSubLotsForPackages() {
  let locationId = props.labelLocationId || form.location_id

  const response = await fetch(
    route('api.packages.sub_lot_options', { packages: props.packages.map((pkg) => pkg.id), location_id: locationId }),
    {
      headers: getHeadersWithToken(),
      method: 'get'
    }
  )

  subLotNums.value = await response.json()
}

const getProductName = (packageId) => {
  let pkg = props.packages.find((pkg) => pkg.id === packageId)
  return pkg?.product_name || pkg?.details?.product_name || null
}
const getPackageName = (packageId) => {
  let pkg = props.packages.find((pkg) => pkg.id === packageId)
  return pkg?.name || pkg?.details?.name || null
}
const getPackageSubLotNum = (packageId) => {
  let pkg = props.packages.find((pkg) => pkg.id === packageId)
  return pkg?.sub_lot_num || null
}

const subLotOptionsForPackage = (packageId) => {
  return subLotNums.value[packageId] || []
}

const printLabel = () => {
  if (submitting.value) {
    return
  }

  submitting.value = true

  form.post(route('packages.print_label'), {
    // errorBag: 'createTransfer',
    onSuccess: (response) => {
      form.reset()
      emit('close')
    },
    onFinish: (response) => {
      submitting.value = false
    }
  })
}

const getErrors = (field) => {
  let errors = form.errors || {}
  return field in errors ? errors[field] : ''
}
</script>
